// kware start edit - Offline Operations: Excel parser for batch checkout/checkin/renew
// REASON: Parses the offline operations Excel template (3 sheets: Check_Out, Check_In, Renew)
// and builds a sorted list of operations to be executed against the FOLIO circulation APIs.

import * as XLSX from 'xlsx';

export const OPERATION_TYPES = {
  CHECK_OUT: 'CHECK_OUT',
  CHECK_IN: 'CHECK_IN',
  RENEW: 'RENEW',
};

const SHEET_NAMES = {
  CHECK_OUT: 'Check_Out',
  CHECK_IN: 'Check_In',
  RENEW: 'Renew',
};

const buildDateTime = (date, time) => {
  if (!date) return null;
  const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  const timeStr = time && time !== '' ? String(time).trim() : '00:00';
  return `${dateStr}T${timeStr}:00.000Z`;
};

const parseSheet = (wb, sheetName, rowMapper) => {
  const ws = wb.Sheets[sheetName];
  if (!ws) return [];

  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  // skip header row (row 0) and instruction rows (rows 1,2 = title + note)
  const dataRows = rows.slice(3);

  return dataRows
    .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined))
    .map((row, index) => rowMapper(row, index))
    .filter(op => op !== null);
};

export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array', cellDates: true });

        const checkOuts = parseSheet(wb, SHEET_NAMES.CHECK_OUT, (row, idx) => {
          const patronBarcode = row[0] ? String(row[0]).trim() : '';
          const itemBarcode = row[1] ? String(row[1]).trim() : '';
          const date = row[2] ? (row[2] instanceof Date ? row[2].toISOString().split('T')[0] : String(row[2]).trim()) : '';
          const time = row[3] ? String(row[3]).trim() : '00:00';
          const note = row[4] ? String(row[4]).trim() : '';

          if (!patronBarcode || !itemBarcode || !date) return null;

          return {
            type: OPERATION_TYPES.CHECK_OUT,
            rowIndex: idx + 1,
            patronBarcode,
            itemBarcode,
            actionDate: buildDateTime(date, time),
            note,
            status: 'pending',
          };
        });

        const checkIns = parseSheet(wb, SHEET_NAMES.CHECK_IN, (row, idx) => {
          const itemBarcode = row[0] ? String(row[0]).trim() : '';
          const date = row[1] ? (row[1] instanceof Date ? row[1].toISOString().split('T')[0] : String(row[1]).trim()) : '';
          const time = row[2] ? String(row[2]).trim() : '00:00';
          const note = row[3] ? String(row[3]).trim() : '';

          if (!itemBarcode || !date) return null;

          return {
            type: OPERATION_TYPES.CHECK_IN,
            rowIndex: idx + 1,
            itemBarcode,
            actionDate: buildDateTime(date, time),
            note,
            status: 'pending',
          };
        });

        const renewals = parseSheet(wb, SHEET_NAMES.RENEW, (row, idx) => {
          const patronBarcode = row[0] ? String(row[0]).trim() : '';
          const itemBarcode = row[1] ? String(row[1]).trim() : '';
          const date = row[2] ? (row[2] instanceof Date ? row[2].toISOString().split('T')[0] : String(row[2]).trim()) : '';
          const time = row[3] ? String(row[3]).trim() : '00:00';
          const note = row[4] ? String(row[4]).trim() : '';

          if (!patronBarcode || !itemBarcode || !date) return null;

          return {
            type: OPERATION_TYPES.RENEW,
            rowIndex: idx + 1,
            patronBarcode,
            itemBarcode,
            actionDate: buildDateTime(date, time),
            note,
            status: 'pending',
          };
        });

        // Sort all operations chronologically
        const allOperations = [...checkOuts, ...checkIns, ...renewals]
          .sort((a, b) => new Date(a.actionDate) - new Date(b.actionDate))
          .map((op, idx) => ({ ...op, id: idx + 1 }));

        resolve({
          operations: allOperations,
          summary: {
            checkOut: checkOuts.length,
            checkIn: checkIns.length,
            renew: renewals.length,
            total: allOperations.length,
          },
        });
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};
// kware end edit
